import { useEffect, useState } from "react";
import fetchWithCredentials from "apiClient";
import { ELORIA_EMAIL } from "constants/eloriaContact";
import {
  wappAssistantModeFromSettings,
  type WappAssistantMode,
} from "utils/othersSection";

type AssistantIdentity = {
  assistantName: string;
  connectedWappPhone: string | null;
  hasConnectedNumber: boolean;
  shareMailAddress: string;
  wappAssistantMode: WappAssistantMode | null;
};

const DEFAULT_ASSISTANT_NAME = "Eloria";

const defaultIdentity = (): AssistantIdentity => ({
  assistantName: DEFAULT_ASSISTANT_NAME,
  connectedWappPhone: null,
  hasConnectedNumber: false,
  shareMailAddress: ELORIA_EMAIL,
  wappAssistantMode: null,
});

let cachedIdentity: AssistantIdentity | null = null;
let pendingFetch: Promise<AssistantIdentity> | null = null;

/** Wait, then try again, after a settings load fails. Stops after these delays. */
const SETTINGS_RETRY_DELAYS_MS = [1000, 3000];

const resolveIdentityFromSettings = (data: any): AssistantIdentity => {
  const hasConnectedNumber = Boolean(data?.wappPhoneNumberId);
  const connectedWappPhone =
    typeof data?.wappPhone === "string" && data.wappPhone.trim()
      ? data.wappPhone.trim()
      : null;
  const customName =
    typeof data?.agentName === "string" ? data.agentName.trim() : "";
  const mailAddress =
    typeof data?.mailAddress === "string" && data.mailAddress.trim()
      ? data.mailAddress.trim()
      : null;
  const defaultMailAddress =
    typeof data?.defaultMailAddress === "string" &&
    data.defaultMailAddress.trim()
      ? data.defaultMailAddress.trim()
      : ELORIA_EMAIL;

  return {
    hasConnectedNumber,
    connectedWappPhone,
    assistantName: customName || DEFAULT_ASSISTANT_NAME,
    shareMailAddress: mailAddress || defaultMailAddress,
    wappAssistantMode: wappAssistantModeFromSettings(data),
  };
};

export const setAssistantIdentityCache = (data: any): AssistantIdentity => {
  const next = resolveIdentityFromSettings(data);
  cachedIdentity = next;
  return next;
};

export const getAssistantIdentityCached = (): AssistantIdentity | null =>
  cachedIdentity;

export const clearAssistantIdentityCache = (): void => {
  cachedIdentity = null;
};

export const fetchAssistantIdentity = async (
  force = false,
): Promise<AssistantIdentity> => {
  if (!force && cachedIdentity) return cachedIdentity;
  if (!force && pendingFetch) return pendingFetch;

  pendingFetch = (async () => {
    try {
      const data = await fetchWithCredentials("/manager-api/property-manager/settings/");
      return setAssistantIdentityCache(data);
    } catch {
      // Leave the cache empty. Storing the fallback pins wappAssistantMode
      // to null, so Otros stays hidden for the session after a blip.
      return cachedIdentity ?? defaultIdentity();
    } finally {
      pendingFetch = null;
    }
  })();

  return pendingFetch;
};

export function useAssistantIdentity(options?: { enabled?: boolean }) {
  const enabled = options?.enabled ?? true;
  const [identity, setIdentity] = useState<AssistantIdentity>(
    () => cachedIdentity || defaultIdentity(),
  );

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    let retryTimer: number | undefined;
    let attempt = 0;

    const load = () => {
      void fetchAssistantIdentity().then((next) => {
        if (cancelled) return;
        setIdentity(next);
        if (next.wappAssistantMode != null) return;
        const delay = SETTINGS_RETRY_DELAYS_MS[attempt];
        if (delay == null) return;
        attempt += 1;
        retryTimer = window.setTimeout(load, delay);
      });
    };

    load();

    return () => {
      cancelled = true;
      if (retryTimer != null) window.clearTimeout(retryTimer);
    };
  }, [enabled]);

  return identity;
}
